import { ApiSysService } from "@services/api/api-sys.service";
import { AuthorizationError } from "../errors/auth-error";
import { BaseError } from "../errors/error-base";
import { ResourceError } from "../errors/resource-error";

/**
 * Get the list of infrastructures
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 */
export async function getInfrastructures(
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfrastructures(token);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });

            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Get the list of infrastructures an user has access to
 * @param {Int} id The ID of the user
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} List of infrastructures
 */
export async function getInfrastructuresByUserId(
    id: number,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfrastructuresByUserId(token, id);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Get the list of infrastructures an organization has access to
 * @param {Int} id The ID of the organization
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} List of infrastructures
 */
export async function getInfrastructuresByOrgId(
    id: number,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfrastructuresByOrgId(token, id);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

export async function assignInfrastructureToOrg(
    orgId: number,
    strId: number,
    catIdArray: number[],
    token: string,
    apiSys: ApiSysService,
    translations: any,
    additionalData?: { mng_tit_array?: string[] | number[] }
) {
    const params: any = {
        org_id: orgId,
        str_id: strId,
        cat_id_array: catIdArray
    };

    if (additionalData?.mng_tit_array !== undefined) {
        params.mng_tit_array = additionalData.mng_tit_array;
    }

    const { data, error } = await apiSys.assignInfrastructureToOrg(
        token,
        params
    );

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }
    return data;
}

/**
 * Assign an infrastructure to a user
 * @param {Int} userId The ID of the user
 * @param {Int} strId The ID of the infrastructure
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} The response data
 */
export async function assignInfrastructureToUser(
    strId: number,
    userId: number,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.assignInfrastructureToUser(
        token,
        userId,
        strId
    );

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Remove an infrastructure from a user
 * @param {Int} userId The ID of the user
 * @param {Int} strId The ID of the infrastructure
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} The response data
 */
export async function removeInfrastructureFromUser(
    strId: number,
    userId: number,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.removeInfrastructureFromUser(
        token,
        userId,
        strId
    );

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "UPDATE_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Remove an infrastructure from an organization
 * @param {Int} orgId The ID of the organization
 * @param {Int} strId The ID of the infrastructure
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} The response data
 */
export async function removeInfrastructureFromOrg(
    strId: number,
    orgId: number,
    catIdArray: number[],
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.removeInfrastructureFromOrg(
        token,
        orgId,
        strId,
        catIdArray
    );

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "UPDATE_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Get the list of infrastructures assigned to an organization
 * @param {Int} id The ID of the organization
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} List of infrastructures
 */
export async function getInfsOfOrg(
    id: number,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfsOfOrg(token, id);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Get the list of infrastructures assigned to an organization
 * @param {Int} id The ID of the organization
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} List of infrastructures
 */
export async function getInfsFilterResults(
    infName: string,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfrastructureFilterResults(token, infName);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Get the list of infrastructures assigned to an organization
 * @param {Int} id The ID of the organization
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {any} List of infrastructures
 */
export async function getInfCode(
    code: string,
    token: string,
    apiSys: ApiSysService,
    translations: any
) {
    const { data, error } = await apiSys.getInfrastructureByCode(token, code);

    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "FETCH_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }

    return data;
}

/**
 * Create a new infrastructure
 * @param {any} strData The infrastructure data
 * @param {string} token The token to be used in the request
 * @param {ApiSysService} apiSys The instance of the "apiSys" service
 * @param {any} generalTranslations The general translations
 * @returns {Promise<any>} The response data
*/
export async function postInfrastructure(
    strData: any,
    token: string,
    apiSys: ApiSysService,
    translations: any,
    lang: string = 'eng'
) {
    const { data, error } = await apiSys.postInfrastructure(token, strData, lang);
    if (error) {
        switch (error.status) {
            case 404:
                throw new AuthorizationError({
                    name: "PERSONNEL_NOT_FOUND",
                    message: translations?.error_404,
                    code: error.status,
                });
            case 500:
                throw new ResourceError({
                    name: "CREATE_INFRASTRUCTURE_ERROR",
                    message: translations?.error_500,
                    cause: error,
                    code: error.status,
                });
            case 400:
                throw new AuthorizationError({
                    name: "INVALID_USER_TYPE",
                    message: translations?.error_400,
                    cause: error,
                    code: error.status,
                });
            default:
                throw new BaseError({
                    name: "UNKNOWN_ERROR",
                    message: translations?.error_unk,
                    cause: error,
                    code: error.status,
                });
        }
    }
    return data;
}
